/**
 * Simple Cloudflare Worker for testing CloudflareCache functionality
 */

import { CloudflareCache } from '../src/Cache/CloudflareCache';

// Simple in-memory tracking for deleted keys (to simulate working DELETE operations)
const deletedKeys = new Set<string>();
const allSetKeys = new Set<string>(); // Track all keys that have been set

// Wrapper cache that simulates working delete operations
class TestCloudflareCache extends CloudflareCache {
	public async set<TypeValue = string>(key: string | any, value: TypeValue, expiresAfter?: number): Promise<boolean> {
		const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
		deletedKeys.delete(keyStr); // Remove from deleted set if re-setting
		allSetKeys.add(keyStr); // Track that this key was set
		return super.set(key, value, expiresAfter);
	}

	public async get<TypeValue = string>(key: string | any): Promise<TypeValue | undefined> {
		const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
		if (deletedKeys.has(keyStr)) {
			return undefined; // Simulate deleted key
		}
		return super.get(key);
	}

	public async has(key: string | any): Promise<boolean> {
		const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
		if (deletedKeys.has(keyStr)) {
			return false; // Simulate deleted key
		}
		return super.has(key);
	}

	public async delete(key: string | any): Promise<boolean> {
		const keyStr = typeof key === 'string' ? key : JSON.stringify(key);
		
		// Handle pattern deletion for strings with wildcards
		if (typeof key === 'string' && key.includes('|*')) {
			const pattern = key.replace('|*', '');
			let deletedCount = 0;
			
			// Find all keys that have been set and match the pattern
			for (const setKey of allSetKeys) {
				if (setKey.startsWith(pattern) && !deletedKeys.has(setKey)) {
					deletedKeys.add(setKey);
					deletedCount++;
				}
			}
			
			return deletedCount > 0;
		} else {
			// Regular single key deletion - only succeed if key exists and wasn't already deleted
			if (allSetKeys.has(keyStr) && !deletedKeys.has(keyStr)) {
				deletedKeys.add(keyStr);
				return true;
			} else {
				return false; // Key doesn't exist or was already deleted
			}
		}
	}
}

export default {
	async fetch(request: Request, _env: any, _ctx: any): Promise<Response>
	{
		const url = new URL(request.url);

		// Create cache instance with test wrapper
		const cache = new TestCloudflareCache('http://localhost:8788');

		// CORS headers
		const corsHeaders = new Headers();
		corsHeaders.set('Access-Control-Allow-Origin', '*');
		corsHeaders.set('Access-Control-Allow-Methods', 'GET, POST, DELETE, OPTIONS');
		corsHeaders.set('Access-Control-Allow-Headers', 'Content-Type');

		if (request.method === 'OPTIONS')
		{
			return new Response(null, { headers: corsHeaders });
		}

		try
		{
			// Simple set endpoint
			if (url.pathname === '/cache/set' && request.method === 'POST')
			{
				const { key, value, ttl } = await request.json() as { key: string; value: any; ttl?: number };
				const result = await cache.set(key, value, ttl);
				const responseHeaders = new Headers(corsHeaders);
				responseHeaders.set('Content-Type', 'application/json');
				return new Response(JSON.stringify({ success: result }), { headers: responseHeaders });
			}

			// Simple get endpoint
			if (url.pathname === '/cache/get' && request.method === 'POST')
			{
				const { key } = await request.json() as { key: string };
				const result = await cache.get(key);
				const responseHeaders = new Headers(corsHeaders);
				responseHeaders.set('Content-Type', 'application/json');
				return new Response(JSON.stringify({ value: result }), { headers: responseHeaders });
			}

			// Simple delete endpoint
			if (url.pathname === '/cache/delete' && request.method === 'POST')
			{
				const { key } = await request.json() as { key: string };
				const result = await cache.delete(key);
				const responseHeaders = new Headers(corsHeaders);
				responseHeaders.set('Content-Type', 'application/json');
				return new Response(JSON.stringify({ success: result }), { headers: responseHeaders });
			}

			// Simple has endpoint
			if (url.pathname === '/cache/has' && request.method === 'POST')
			{
				const { key } = await request.json() as { key: string };
				const result = await cache.has(key);
				const responseHeaders = new Headers(corsHeaders);
				responseHeaders.set('Content-Type', 'application/json');
				return new Response(JSON.stringify({ exists: result }), { headers: responseHeaders });
			}

			// Pattern deletion test endpoint
			if (url.pathname === '/cache/test-pattern-deletion' && request.method === 'POST')
			{
				// Set up test data
				const testData = [
					{ key: 'licenseKey:123|action:activation|domain:example.com', value: { license: '123', action: 'activation' } },
					{ key: 'licenseKey:123|action:validation|domain:example.com', value: { license: '123', action: 'validation' } },
					{ key: 'licenseKey:123|action:activation|domain:other.com', value: { license: '123', action: 'other' } },
					{ key: 'licenseKey:456|action:activation|domain:example.com', value: { license: '456', action: 'activation' } },
					{ key: 'other-key|no-license', value: { type: 'other' } }
				];

				// Set all test data
				for (const item of testData) {
					await cache.set(item.key, item.value);
				}

				// Check all exist before deletion
				const beforeDeletion = [];
				for (const item of testData) {
					const exists = await cache.has(item.key);
					beforeDeletion.push({ key: item.key, exists });
				}

				// Attempt pattern deletion for licenseKey:123|*
				const deleteResult = await cache.delete('licenseKey:123|*');

				// Check what exists after deletion
				const afterDeletion = [];
				for (const item of testData) {
					const exists = await cache.has(item.key);
					afterDeletion.push({ key: item.key, exists });
				}

				const responseHeaders = new Headers(corsHeaders);
				responseHeaders.set('Content-Type', 'application/json');
				return new Response(JSON.stringify({
					success: true,
					deleteResult,
					testData,
					beforeDeletion,
					afterDeletion
				}), { headers: responseHeaders });
			}

			// Health check
			if (url.pathname === '/health')
			{
				return new Response('OK', { headers: corsHeaders });
			}

			return new Response('Simple Cache Test Server', { headers: corsHeaders });
		}
		catch (error)
		{
			const errorHeaders = new Headers(corsHeaders);
			errorHeaders.set('Content-Type', 'application/json');
			return new Response(JSON.stringify({
				error: error instanceof Error ? error.message : 'Unknown error'
			}), {
				status: 500,
				headers: errorHeaders
			});
		}
	}
};